// Zustand store holding the current user + memberships, plus the helpers the
// router uses to make beforeLoad redirect decisions.

import { create } from "zustand";
import type { Tenant, User } from "@jotty/api-client";

import { ApiError, auth, tenants } from "./api";

type AuthStatus = "unknown" | "loading" | "authenticated" | "anonymous";

interface AuthState {
  status: AuthStatus;
  user: User | null;
  tenants: Tenant[];
  /** Hydrate user + tenants from the API. Idempotent — safe to call repeatedly. */
  refresh: () => Promise<void>;
  /** Replace state after a successful sign-in. Triggers a follow-up tenants list. */
  setUser: (user: User) => Promise<void>;
  /** Clear state (used on sign-out + on 401 from /me). */
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "unknown",
  user: null,
  tenants: [],

  async refresh() {
    if (get().status === "loading") return;
    set({ status: "loading" });
    try {
      const user = await auth.me();
      const list = await tenants.list();
      set({ status: "authenticated", user, tenants: list.tenants ?? [] });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        set({ status: "anonymous", user: null, tenants: [] });
        return;
      }
      // Network or 5xx — treat as anonymous so the UI stays responsive.
      // Toast or banner could be added here later.
      console.error("auth refresh failed", err);
      set({ status: "anonymous", user: null, tenants: [] });
    }
  },

  async setUser(user) {
    set({ status: "authenticated", user });
    try {
      const list = await tenants.list();
      set({ tenants: list.tenants ?? [] });
    } catch (err) {
      console.error("tenants list after sign-in failed", err);
      set({ tenants: [] });
    }
  },

  clear() {
    set({ status: "anonymous", user: null, tenants: [] });
  },
}));
